import { openDB } from 'idb'

const DB_NAME = 'CantinaDB'
const DB_VERSION = 1

// Nomes das stores
const STORES = {
  VENDAS_PENDENTES: 'vendasPendentes',
  CLIENTES_CACHE: 'clientesCache',
  PRODUTOS_CACHE: 'produtosCache',
  SYNC_QUEUE: 'syncQueue'
}

class OfflineStorage {
  constructor() {
    this.db = null
    this.initPromise = this.init()
  }

  async init() {
    try {
      this.db = await openDB(DB_NAME, DB_VERSION, {
        upgrade(db) {
          // Store para vendas pendentes de sincronização
          if (!db.objectStoreNames.contains(STORES.VENDAS_PENDENTES)) {
            const vendasStore = db.createObjectStore(STORES.VENDAS_PENDENTES, { keyPath: 'syncId' })
            vendasStore.createIndex('createdAt', 'createdAt')
            vendasStore.createIndex('synced', 'synced')
          }

          // Store para cache de clientes
          if (!db.objectStoreNames.contains(STORES.CLIENTES_CACHE)) {
            db.createObjectStore(STORES.CLIENTES_CACHE, { keyPath: 'id' })
          }

          // Store para cache de produtos
          if (!db.objectStoreNames.contains(STORES.PRODUTOS_CACHE)) {
            const produtosStore = db.createObjectStore(STORES.PRODUTOS_CACHE, { keyPath: 'id' })
            produtosStore.createIndex('nome', 'nome')
          }

          // Store para fila de sincronização
          if (!db.objectStoreNames.contains(STORES.SYNC_QUEUE)) {
            const queueStore = db.createObjectStore(STORES.SYNC_QUEUE, { keyPath: 'id', autoIncrement: true })
            queueStore.createIndex('type', 'type')
            queueStore.createIndex('createdAt', 'createdAt')
          }
        }
      })
      
      console.log('IndexedDB inicializado com sucesso')
      return true
    } catch (error) {
      console.error('Erro ao inicializar IndexedDB:', error)
      return false
    }
  }

  // ============ VENDAS PENDENTES ============
  
  async salvarVendaPendente(venda) {
    await this.initPromise
    
    const syncId = `local_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    const vendaPendente = {
      syncId,
      ...venda,
      createdOffline: true,
      createdAt: new Date().toISOString(),
      synced: false,
      tentativasSync: 0
    }
    
    await this.db.put(STORES.VENDAS_PENDENTES, vendaPendente)
    return vendaPendente
  }

  async listarVendasPendentes() {
    await this.initPromise
    return await this.db.getAllFromIndex(STORES.VENDAS_PENDENTES, 'synced', false)
  }

  async contarVendasPendentes() {
    await this.initPromise
    const vendas = await this.listarVendasPendentes()
    return vendas.length
  }

  async marcarVendaSyncronizada(syncId) {
    await this.initPromise
    const venda = await this.db.get(STORES.VENDAS_PENDENTES, syncId)
    if (venda) {
      venda.synced = true
      venda.syncedAt = new Date().toISOString()
      await this.db.put(STORES.VENDAS_PENDENTES, venda)
    }
  }

  async removerVendaPendente(syncId) {
    await this.initPromise
    await this.db.delete(STORES.VENDAS_PENDENTES, syncId)
  }

  async incrementarTentativa(syncId) {
    await this.initPromise
    const venda = await this.db.get(STORES.VENDAS_PENDENTES, syncId)
    if (venda) {
      venda.tentativasSync = (venda.tentativasSync || 0) + 1
      await this.db.put(STORES.VENDAS_PENDENTES, venda)
    }
  }

  // ============ CACHE DE CLIENTES ============
  
  async salvarClientesCache(clientes) {
    await this.initPromise
    const tx = this.db.transaction(STORES.CLIENTES_CACHE, 'readwrite')
    
    for (const cliente of clientes) {
      await tx.store.put({
        ...cliente,
        cachedAt: new Date().toISOString()
      })
    }
    
    await tx.done
  }

  async buscarClientesCache() {
    await this.initPromise
    return await this.db.getAll(STORES.CLIENTES_CACHE)
  }

  async buscarClienteCachePorId(id) {
    await this.initPromise
    return await this.db.get(STORES.CLIENTES_CACHE, id)
  }

  async buscarClientesCachePorNome(nome) {
    await this.initPromise
    const clientes = await this.db.getAll(STORES.CLIENTES_CACHE)
    const termo = nome.toLowerCase()
    return clientes.filter(c => 
      c.nome.toLowerCase().includes(termo) || 
      (c.responsavel && c.responsavel.toLowerCase().includes(termo))
    )
  }

  // ============ CACHE DE PRODUTOS ============
  
  async salvarProdutosCache(produtos) {
    await this.initPromise
    const tx = this.db.transaction(STORES.PRODUTOS_CACHE, 'readwrite')
    
    for (const produto of produtos) {
      await tx.store.put({
        ...produto,
        cachedAt: new Date().toISOString()
      })
    }
    
    await tx.done
  }

  async buscarProdutosCache() {
    await this.initPromise
    return await this.db.getAll(STORES.PRODUTOS_CACHE)
  }

  async buscarProdutoCachePorId(id) {
    await this.initPromise
    return await this.db.get(STORES.PRODUTOS_CACHE, id)
  }

  async buscarProdutosCachePorNome(nome) {
    await this.initPromise
    const produtos = await this.db.getAll(STORES.PRODUTOS_CACHE)
    const termo = nome.toLowerCase()
    return produtos.filter(p => 
      p.nome.toLowerCase().includes(termo) ||
      (p.codigo_barras && p.codigo_barras.includes(termo))
    )
  }

  // ============ LIMPEZA ============
  
  async limparVendasSincronizadas() {
    await this.initPromise
    const vendas = await this.db.getAllFromIndex(STORES.VENDAS_PENDENTES, 'synced', true)
    const tx = this.db.transaction(STORES.VENDAS_PENDENTES, 'readwrite')
    
    for (const venda of vendas) {
      // Remover vendas sincronizadas há mais de 7 dias
      const syncedAt = new Date(venda.syncedAt)
      const diasDiff = (Date.now() - syncedAt.getTime()) / (1000 * 60 * 60 * 24)
      
      if (diasDiff > 7) {
        await tx.store.delete(venda.syncId)
      }
    }
    
    await tx.done
  }

  async limparCacheAntigo(dias = 7) {
    await this.initPromise
    const cutoffDate = new Date(Date.now() - dias * 24 * 60 * 60 * 1000).toISOString()
    
    // Limpar cache de clientes
    const clientes = await this.db.getAll(STORES.CLIENTES_CACHE)
    const txClientes = this.db.transaction(STORES.CLIENTES_CACHE, 'readwrite')
    
    for (const cliente of clientes) {
      if (cliente.cachedAt < cutoffDate) {
        await txClientes.store.delete(cliente.id)
      }
    }
    await txClientes.done
    
    // Limpar cache de produtos
    const produtos = await this.db.getAll(STORES.PRODUTOS_CACHE)
    const txProdutos = this.db.transaction(STORES.PRODUTOS_CACHE, 'readwrite')
    
    for (const produto of produtos) {
      if (produto.cachedAt < cutoffDate) {
        await txProdutos.store.delete(produto.id)
      }
    }
    await txProdutos.done
  }

  async limparTudo() {
    await this.initPromise
    
    const tx = this.db.transaction([
      STORES.VENDAS_PENDENTES,
      STORES.CLIENTES_CACHE,
      STORES.PRODUTOS_CACHE,
      STORES.SYNC_QUEUE
    ], 'readwrite')
    
    await Promise.all([
      tx.objectStore(STORES.VENDAS_PENDENTES).clear(),
      tx.objectStore(STORES.CLIENTES_CACHE).clear(),
      tx.objectStore(STORES.PRODUTOS_CACHE).clear(),
      tx.objectStore(STORES.SYNC_QUEUE).clear()
    ])
    
    await tx.done
  }

  // ============ STATUS ============
  
  async getStorageInfo() {
    await this.initPromise
    
    const [vendasPendentes, clientesCache, produtosCache] = await Promise.all([
      this.listarVendasPendentes(),
      this.buscarClientesCache(),
      this.buscarProdutosCache()
    ])
    
    return {
      vendasPendentes: vendasPendentes.length,
      clientesCache: clientesCache.length,
      produtosCache: produtosCache.length,
      temDadosOffline: vendasPendentes.length > 0
    }
  }
}

export default new OfflineStorage()
export { STORES }
